import Radio from '@/components/UI/FormElements/Radio';
import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { type FC } from 'react';
import { featuresSelector } from '@/support/Selectors';
import { ChangePercentage } from '@/components/UI/Table/Reusable/APITable/APITable.model';

const featureSelector = createSelector([featuresSelector], (features) => features?.date_based_alerts ?? false);

interface MetricChangeFromProps {
  value: ChangePercentage;
  onChange: (value: ChangePercentage) => void;
}

/**
 * Component for selecting how percentage metric changes are calculated.
 */
const MetricChangeFrom: FC<MetricChangeFromProps> = ({ value, onChange }) => {
  const dateBasedAlertsEnabled = useSelector(featureSelector);

  return (
    <div className="pa3 bb b--moon-gray">
      <PercentageChange value={value} onChange={onChange} dateBasedAlertsEnabled={dateBasedAlertsEnabled} />
    </div>
  );
};

interface PercentageChangeProps {
  value: ChangePercentage;
  onChange: (value: ChangePercentage) => void;
  dateBasedAlertsEnabled?: boolean;
}

export const PercentageChange: FC<PercentageChangeProps> = ({ value, onChange, dateBasedAlertsEnabled = false }) => {
  const baseOptions: Record<ChangePercentage, string> = {
    [ChangePercentage.FirstTaken]: 'First taken',
    [ChangePercentage.LastTaken]: 'Last taken',
    [ChangePercentage.TrackingDate]: 'Tracking date',
    [ChangePercentage.DiseaseInductionDate]: 'Disease induction date',
  };

  const options: Partial<Record<ChangePercentage, string>> = dateBasedAlertsEnabled
    ? baseOptions
    : {
        [ChangePercentage.FirstTaken]: baseOptions[ChangePercentage.FirstTaken],
        [ChangePercentage.LastTaken]: baseOptions[ChangePercentage.LastTaken],
      };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    onChange(event.target.value as ChangePercentage);
  };

  if (dateBasedAlertsEnabled) {
    return (
      <div className="flex items-center">
        <div className="f6 near-black lh-title mr3">% Metric change from</div>
        <select value={value} onChange={handleChange} className="mb0">
          {(Object.keys(options) as Array<ChangePercentage>).map((key) => (
            <option key={key} value={key}>
              {options[key]}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <div className="f6 near-black lh-title mr3">% Metric change from</div>
      <div className="flex flex-wrap">
        {(Object.keys(options) as Array<ChangePercentage>).map((key) => (
          <div key={key} className="dib mr3">
            <Radio
              id={key}
              name="options"
              checked={value === key}
              label={options[key]}
              onChange={handleChange}
              value={key}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default MetricChangeFrom;
